import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  CreditCard, 
  Shield, 
  ShieldAlert, 
  CheckCircle, 
  XCircle, 
  Eye, 
  EyeOff, 
  Copy, 
  Save, 
  Loader2, 
  AlertCircle, 
  Info,
  Check,
  AlertTriangle
} from "lucide-react";
import { NotificationContext } from "../../context/NotificationContext";
import authAxios from "../../api/authAxios";

export default function RazorpayConfig() {
  const { addNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(true);
  
  // Settings state from backend
  const [settings, setSettings] = useState({
    razorpay_enabled: true,
    razorpay_live_mode: false,
    razorpay_test_key_id: "",
    razorpay_test_key_secret: "",
    razorpay_live_key_id: "",
    razorpay_live_key_secret: "",
    razorpay_webhook_secret: "",
    razorpay_keys_updated_at: null
  });

  // Local editing states (separate for Test/Live cards)
  const [testKeyId, setTestKeyId] = useState("");
  const [testKeySecret, setTestKeySecret] = useState("");
  const [liveKeyId, setLiveKeyId] = useState("");
  const [liveKeySecret, setLiveKeySecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [razorpayEnabled, setRazorpayEnabled] = useState(true);

  // UI state
  const [showTestSecret, setShowTestSecret] = useState(false);
  const [showLiveSecret, setShowLiveSecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  
  const [savingTest, setSavingTest] = useState(false);
  const [savingLive, setSavingLive] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  
  const [testingTest, setTestingTest] = useState(false);
  const [testingLive, setTestingLive] = useState(false);

  const [copiedField, setCopiedField] = useState("");
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get("/api/super-admin/settings-api/");
      const data = response.data;
      setSettings(data);
      
      // Initialize local states
      setTestKeyId(data.razorpay_test_key_id || "");
      setTestKeySecret(data.razorpay_test_key_secret || "");
      setLiveKeyId(data.razorpay_live_key_id || "");
      setLiveKeySecret(data.razorpay_live_key_secret || "");
      setWebhookSecret(data.razorpay_webhook_secret || "");
      setRazorpayEnabled(data.razorpay_enabled ?? true);
    } catch (error) {
      addNotification("Failed to load Razorpay configurations", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    addNotification("Copied to clipboard", "success");
    setTimeout(() => setCopiedField(""), 2000);
  };

  const handleSaveTestCredentials = async () => {
    if (!testKeyId.trim() || !testKeySecret.trim()) {
      addNotification("Test Key ID and Secret cannot be empty", "error");
      return;
    }
    try {
      setSavingTest(true);
      const payload = {
        razorpay_test_key_id: testKeyId,
        razorpay_test_key_secret: testKeySecret
      };
      const response = await authAxios.patch("/api/super-admin/settings-api/", payload);
      setSettings(response.data);
      addNotification("Test credentials saved successfully", "success");
    } catch (error) {
      addNotification("Failed to save Test credentials", "error");
    } finally {
      setSavingTest(false);
    }
  };

  const handleSaveLiveCredentials = async () => {
    if (!liveKeyId.trim() || !liveKeySecret.trim()) {
      addNotification("Live Key ID and Secret cannot be empty", "error");
      return;
    }
    try {
      setSavingLive(true);
      const payload = {
        razorpay_live_key_id: liveKeyId,
        razorpay_live_key_secret: liveKeySecret
      };
      const response = await authAxios.patch("/api/super-admin/settings-api/", payload);
      setSettings(response.data);
      addNotification("Live credentials saved successfully", "success");
    } catch (error) {
      addNotification("Failed to save Live credentials", "error");
    } finally {
      setSavingLive(false);
    }
  };

  const handleSaveWebhookAndEnable = async () => {
    try {
      setSavingWebhook(true);
      const payload = {
        razorpay_webhook_secret: webhookSecret,
        razorpay_enabled: razorpayEnabled
      };
      const response = await authAxios.patch("/api/super-admin/settings-api/", payload);
      setSettings(response.data);
      addNotification("Additional settings saved successfully", "success");
    } catch (error) {
      addNotification("Failed to save additional settings", "error");
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleTestCredentials = async (mode) => {
    const isLive = mode === "live";
    const key_id = isLive ? liveKeyId : testKeyId;
    const key_secret = isLive ? liveKeySecret : testKeySecret;

    if (!key_id.trim() || !key_secret.trim()) {
      addNotification("Key ID and Key Secret are required to test connection", "error");
      return;
    }

    if (isLive) setTestingLive(true);
    else setTestingTest(true);

    try {
      const response = await authAxios.post("/api/super-admin/settings/test-razorpay/", {
        key_id,
        key_secret
      });
      if (response.data.status === "success") {
        addNotification(response.data.message || "Connection successful!", "success");
      } else {
        addNotification(response.data.message || "Connection failed", "error");
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.response?.data?.error || "Connection test failed";
      addNotification(errMsg, "error");
    } finally {
      if (isLive) setTestingLive(false);
      else setTestingTest(false);
    }
  };

  const handleModeSwitch = async (liveMode) => {
    // If switching to live mode, show confirmation popup first
    if (liveMode && !settings.razorpay_live_mode) {
      setShowLiveConfirm(true);
      return;
    }
    
    await executeModeSwitch(liveMode);
  };

  const executeModeSwitch = async (liveMode) => {
    try {
      const payload = {
        razorpay_live_mode: liveMode
      };
      const response = await authAxios.patch("/api/super-admin/settings-api/", payload);
      setSettings(response.data);
      addNotification(`Switched to ${liveMode ? "Live" : "Test"} Mode successfully`, "success");
    } catch (error) {
      addNotification("Failed to switch payment mode", "error");
    } finally {
      setShowLiveConfirm(false);
    }
  };

  // Determine overall configuration status
  const getConfigStatus = () => {
    const activeIsLive = settings.razorpay_live_mode;
    const keyId = activeIsLive ? settings.razorpay_live_key_id : settings.razorpay_test_key_id;
    const secret = activeIsLive ? settings.razorpay_live_key_secret : settings.razorpay_test_key_secret;

    if (!settings.razorpay_enabled) {
      return { label: "Disabled", color: "bg-gray-100 text-gray-700 border-gray-200" };
    }

    if (keyId && secret) {
      return { label: "Fully Configured", color: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    } else if (keyId || secret) {
      return { label: "Partially Configured", color: "bg-amber-50 text-amber-700 border-amber-200" };
    } else {
      return { label: "Not Configured", color: "bg-rose-50 text-rose-700 border-rose-200" };
    }
  };

  const configStatus = getConfigStatus();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin w-8 h-8 text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-24">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-200 pb-5">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm flex items-center justify-center">
            {/* Razorpay SVG Logo */}
            <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.5 12L17.5 3H12.5L15 7.5H7.5L5 3H0L5 12L0 21H5L7.5 16.5H15L12.5 21H17.5L22.5 12Z" fill="#0B44B3" />
              <path d="M15 7.5L12.5 12L15 16.5H7.5L9.5 13H5L7.5 7.5H15Z" fill="#3B82F6" />
            </svg>
          </div>
          <div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Admin &rarr; Payment Settings &rarr; Razorpay Configuration
            </span>
            <div className="flex items-center gap-3 mt-1">
              <h1 className="text-3xl font-black text-gray-900 tracking-tight">Razorpay Configuration</h1>
              <span className={`px-3 py-1 rounded-full text-xs font-black uppercase border ${configStatus.color}`}>
                {configStatus.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Security notice warning */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 flex items-start gap-4 shadow-sm">
        <ShieldAlert className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-bold text-amber-900 text-sm">Security Notice</h3>
          <p className="text-amber-700 text-xs mt-1 leading-relaxed">
            Never share your Razorpay Secret Key with anyone. Store and edit credentials securely. Access to credentials is restricted to system administrators.
          </p>
        </div>
      </div>

      {/* Razorpay Mode Section */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div>
            <h2 className="text-lg font-black text-gray-900">Razorpay Environment Mode</h2>
            <p className="text-xs text-gray-500 mt-1">Switch between testing sandbox and live real-time transactions</p>
          </div>
          
          {/* Segmented Control */}
          <div className="flex bg-gray-100 p-1 rounded-xl w-fit border border-gray-200/50">
            <button
              onClick={() => handleModeSwitch(false)}
              className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
                !settings.razorpay_live_mode
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              Test Mode
            </button>
            <button
              onClick={() => handleModeSwitch(true)}
              className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
                settings.razorpay_live_mode
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              Live Mode
            </button>
          </div>
        </div>

        {/* Status Card Below Segmented Control */}
        <AnimatePresence mode="wait">
          {!settings.razorpay_live_mode ? (
            <motion.div
              key="test-status"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-amber-50/70 border border-amber-200 rounded-xl p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-xs font-bold text-amber-800">
                  Test Mode Active &mdash; No real payments will be processed. Use Razorpay test credentials.
                </span>
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider text-amber-700 bg-amber-100 px-2.5 py-1 rounded-md">
                Sandbox
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="live-status"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-emerald-50/70 border border-emerald-200 rounded-xl p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-bold text-emerald-800">
                  Live Mode Active &mdash; Real customer transactions will be processed using live credentials.
                </span>
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-md">
                Production
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Double Credential Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Test Mode Credentials Card */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex flex-col justify-between space-y-6 relative overflow-hidden">
          {!settings.razorpay_live_mode && (
            <div className="absolute top-0 right-0 bg-amber-500 text-white font-bold text-[9px] uppercase tracking-wider px-3 py-1 rounded-bl-lg">
              Active Environment
            </div>
          )}
          <div className="space-y-4">
            <div className="border-b border-gray-100 pb-3">
              <h3 className="text-md font-black text-gray-900 flex items-center gap-2">
                <span>Test Mode Credentials</span>
                {!settings.razorpay_live_mode && <CheckCircle className="w-4 h-4 text-amber-500" />}
              </h3>
              <p className="text-xs text-gray-500 mt-1">Used during integration testing and validation sandbox</p>
            </div>

            {/* Test Key ID */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Test Key ID</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={testKeyId}
                  onChange={(e) => setTestKeyId(e.target.value)}
                  placeholder="rzp_test_..."
                  className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                />
                <button
                  type="button"
                  onClick={() => handleCopy(testKeyId, "test_key_id")}
                  className="absolute right-3 text-gray-400 hover:text-gray-600 transition"
                  title="Copy Key ID"
                >
                  {copiedField === "test_key_id" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              {!testKeyId.trim() && (
                <span className="text-[10px] text-amber-600 font-bold flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Key ID is required
                </span>
              )}
            </div>

            {/* Test Key Secret */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Test Key Secret</label>
              <div className="relative flex items-center">
                <input
                  type={showTestSecret ? "text" : "password"}
                  value={testKeySecret}
                  onChange={(e) => setTestKeySecret(e.target.value)}
                  placeholder="••••••••••••••••••••••••"
                  className="w-full pl-4 pr-24 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                />
                <div className="absolute right-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowTestSecret(!showTestSecret)}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    {showTestSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopy(testKeySecret, "test_key_secret")}
                    className="text-gray-400 hover:text-gray-600 transition"
                    title="Copy Key Secret"
                  >
                    {copiedField === "test_key_secret" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {!testKeySecret.trim() && (
                <span className="text-[10px] text-amber-600 font-bold flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Key Secret is required
                </span>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-4">
            <button
              onClick={() => handleTestCredentials("test")}
              disabled={testingTest}
              className="px-4 py-2 border.5 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl text-xs font-bold transition flex items-center gap-2 border disabled:opacity-50"
            >
              {testingTest ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Testing...
                </>
              ) : (
                "Test Connection"
              )}
            </button>
            <button
              onClick={handleSaveTestCredentials}
              disabled={savingTest}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 shadow-md hover:shadow-lg transition disabled:opacity-50"
            >
              {savingTest ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  Save Test Keys
                </>
              )}
            </button>
          </div>
        </div>

        {/* Live Mode Credentials Card */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex flex-col justify-between space-y-6 relative overflow-hidden">
          {settings.razorpay_live_mode && (
            <div className="absolute top-0 right-0 bg-emerald-500 text-white font-bold text-[9px] uppercase tracking-wider px-3 py-1 rounded-bl-lg">
              Active Environment
            </div>
          )}
          <div className="space-y-4">
            <div className="border-b border-gray-100 pb-3">
              <h3 className="text-md font-black text-gray-900 flex items-center gap-2">
                <span>Live Mode Credentials</span>
                {settings.razorpay_live_mode && <CheckCircle className="w-4 h-4 text-emerald-500" />}
              </h3>
              <p className="text-xs text-gray-500 mt-1">Used to process real customer payments and settlements</p>
            </div>

            {/* Live Key ID */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Live Key ID</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={liveKeyId}
                  onChange={(e) => setLiveKeyId(e.target.value)}
                  placeholder="rzp_live_..."
                  className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                />
                <button
                  type="button"
                  onClick={() => handleCopy(liveKeyId, "live_key_id")}
                  className="absolute right-3 text-gray-400 hover:text-gray-600 transition"
                  title="Copy Key ID"
                >
                  {copiedField === "live_key_id" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              {!liveKeyId.trim() && (
                <span className="text-[10px] text-amber-600 font-bold flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Key ID is required for live transactions
                </span>
              )}
            </div>

            {/* Live Key Secret */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Live Key Secret</label>
              <div className="relative flex items-center">
                <input
                  type={showLiveSecret ? "text" : "password"}
                  value={liveKeySecret}
                  onChange={(e) => setLiveKeySecret(e.target.value)}
                  placeholder="••••••••••••••••••••••••"
                  className="w-full pl-4 pr-24 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                />
                <div className="absolute right-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowLiveSecret(!showLiveSecret)}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    {showLiveSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopy(liveKeySecret, "live_key_secret")}
                    className="text-gray-400 hover:text-gray-600 transition"
                    title="Copy Key Secret"
                  >
                    {copiedField === "live_key_secret" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {!liveKeySecret.trim() && (
                <span className="text-[10px] text-amber-600 font-bold flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Key Secret is required for live transactions
                </span>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-4">
            <button
              onClick={() => handleTestCredentials("live")}
              disabled={testingLive}
              className="px-4 py-2 border.5 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl text-xs font-bold transition flex items-center gap-2 border disabled:opacity-50"
            >
              {testingLive ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Testing...
                </>
              ) : (
                "Test Connection"
              )}
            </button>
            <button
              onClick={handleSaveLiveCredentials}
              disabled={savingLive}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 shadow-md hover:shadow-lg transition disabled:opacity-50"
            >
              {savingLive ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  Save Live Keys
                </>
              )}
            </button>
          </div>
        </div>

      </div>

      {/* Additional Settings & Global Config Card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6">
        <div className="border-b border-gray-100 pb-3">
          <h3 className="text-md font-black text-gray-900">Additional Settings</h3>
          <p className="text-xs text-gray-500 mt-1">Configure webhooks and global payment availability controls</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Webhook Secret field */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Webhook Secret</label>
            <div className="relative flex items-center">
              <input
                type={showWebhookSecret ? "text" : "password"}
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="whsec_..."
                className="w-full pl-4 pr-24 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all font-mono"
              />
              <div className="absolute right-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => handleCopy(webhookSecret, "webhook_secret")}
                  className="text-gray-400 hover:text-gray-600 transition"
                  title="Copy Webhook Secret"
                >
                  {copiedField === "webhook_secret" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 font-medium">Used to sign webhook payloads to confirm signature authentication.</p>
          </div>

          {/* Enable/Disable Razorpay Checkbox */}
          <div className="flex items-center">
            <motion.label className="flex items-center p-5 w-full bg-slate-50 border border-slate-200 hover:bg-slate-100 rounded-xl cursor-pointer transition-all duration-200 group" whileHover={{ x: 4 }}>
              <div className="relative flex items-center h-6 w-6">
                <input
                  type="checkbox"
                  checked={razorpayEnabled}
                  onChange={() => setRazorpayEnabled(!razorpayEnabled)}
                  className="w-6 h-6 rounded-lg cursor-pointer accent-blue-600 appearance-none bg-white border-2 border-slate-200 checked:bg-blue-600 checked:border-blue-600 transition-all hover:border-blue-400"
                />
                {razorpayEnabled && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-white">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" /></svg>
                  </div>
                )}
              </div>
              <div className="ml-5">
                <span className="text-sm font-black text-slate-800 group-hover:text-slate-900 transition">Enable Razorpay Payment Gateway</span>
                <p className="text-[10px] text-slate-400 font-medium mt-0.5">Toggle to globally activate or suspend Razorpay billing payments</p>
              </div>
            </motion.label>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-4">
          <div className="text-[11px] text-gray-400 font-semibold flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5" />
            <span>Credentials updated: {settings.razorpay_keys_updated_at ? new Date(settings.razorpay_keys_updated_at).toLocaleString() : "Never"}</span>
          </div>
          <button
            onClick={handleSaveWebhookAndEnable}
            disabled={savingWebhook}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 shadow-md hover:shadow-lg transition disabled:opacity-50"
          >
            {savingWebhook ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                Save Additional Settings
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirmation Modal for Live Mode */}
      <AnimatePresence>
        {showLiveConfirm && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-6"
            >
              <div className="flex items-center gap-4 text-amber-600 bg-amber-50 p-4 rounded-2xl border border-amber-100">
                <AlertTriangle className="w-8 h-8 flex-shrink-0" />
                <div>
                  <h4 className="font-black text-sm text-amber-900">Switch to Live Production Mode?</h4>
                  <p className="text-xs text-amber-700 mt-0.5">Please review before enabling live gateway payments.</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs text-slate-600 leading-relaxed font-semibold">
                  By switching to Live Mode, you will activate real customer billing transactions. Any transaction fees or credit card payments will settle to your production Razorpay account.
                </p>
                <p className="text-xs text-slate-500 italic">
                  Ensure that you have inputted your production key credentials and configured the corresponding webhook endpoint in the Razorpay Dashboard.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  onClick={() => setShowLiveConfirm(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl text-xs font-bold transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => executeModeSwitch(true)}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md hover:shadow-lg transition"
                >
                  Confirm Live Mode
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
